/**
 * Bug Log Template Form
 * Bug tracking and management
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const BugLogForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', date: new Date().toISOString().split('T')[0], prepared_by: '',
    bugs: [{ bug_id: '', title: '', description: '', steps_to_reproduce: '', expected_result: '', actual_result: '', severity: 'Medium', priority: 'Medium', status: 'Open', environment: '', browser: '', reported_by: '', reported_date: '', assigned_to: '', fixed_date: '', verified_date: '', notes: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'E-Commerce Platform v2.0',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'QA Team',
    bugs: [
      { bug_id: 'BUG-001', title: 'Cart total not updating on quantity change', description: 'When user changes item quantity, the cart total does not update until page refresh', steps_to_reproduce: '1. Add item to cart\n2. Change quantity to 2\n3. Observe total', expected_result: 'Total updates immediately to reflect new quantity', actual_result: 'Total stays the same until page refresh', severity: 'High', priority: 'High', status: 'In Progress', environment: 'Production', browser: 'Chrome 120', reported_by: 'Jane Doe', reported_date: '2025-01-10', assigned_to: 'John Smith', fixed_date: '', verified_date: '', notes: 'Related to cart state management' },
      { bug_id: 'BUG-002', title: 'Login button unresponsive on mobile', description: 'Login button does not respond to taps on iOS Safari', steps_to_reproduce: '1. Open login page on iPhone\n2. Enter credentials\n3. Tap Login button', expected_result: 'Login form submits', actual_result: 'Nothing happens on tap', severity: 'Critical', priority: 'Critical', status: 'Open', environment: 'Production', browser: 'Safari iOS 17', reported_by: 'Alex Kumar', reported_date: '2025-01-12', assigned_to: '', fixed_date: '', verified_date: '', notes: 'Affects all iOS users' },
      { bug_id: 'BUG-003', title: 'Order confirmation email missing items', description: 'Order confirmation email only shows first item, not full order', steps_to_reproduce: '1. Add 3 items to cart\n2. Complete checkout\n3. Check confirmation email', expected_result: 'Email shows all 3 items', actual_result: 'Email only shows 1 item', severity: 'Medium', priority: 'High', status: 'Fixed', environment: 'Staging', browser: 'N/A', reported_by: 'Sarah Chen', reported_date: '2025-01-08', assigned_to: 'Mike Wilson', fixed_date: '2025-01-11', verified_date: '', notes: 'Email template issue' },
      { bug_id: 'BUG-004', title: 'Search results pagination broken', description: 'Clicking page 2 in search results shows same items as page 1', steps_to_reproduce: '1. Search for "shoes"\n2. Click page 2', expected_result: 'Shows items 11-20', actual_result: 'Shows items 1-10 again', severity: 'Medium', priority: 'Medium', status: 'Verified', environment: 'Production', browser: 'All', reported_by: 'QA Team', reported_date: '2025-01-05', assigned_to: 'Lisa Park', fixed_date: '2025-01-09', verified_date: '2025-01-10', notes: '' },
      { bug_id: 'BUG-005', title: 'Product images not loading on slow connection', description: 'Images show broken icon on 3G connection speed', steps_to_reproduce: '1. Throttle network to Slow 3G\n2. Navigate to product page', expected_result: 'Images load (may be slow)', actual_result: 'Images show broken icon', severity: 'Low', priority: 'Low', status: 'Backlog', environment: 'Production', browser: 'All', reported_by: 'David Kim', reported_date: '2025-01-13', assigned_to: '', fixed_date: '', verified_date: '', notes: 'Image timeout too short' }
    ],
    rai_assignments: [
      { task: 'Triage new bugs daily', responsible: 'QA Lead', accountable: 'Engineering Manager', informed: 'Product Owner', due_date: '2025-01-20', status: 'In Progress' }
    ],
    followups: [
      { item: 'Fix critical iOS login bug', owner: 'Mobile Lead', due_date: '2025-01-15', priority: 'Critical', status: 'In Progress' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Bug Log Draft";
      const payload = {
        name: draftName,
        template_type: "Bug Log",
        template_id: "bug-log",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "bug-log", "Bug Log", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("bug-log", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/bug-log`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Bug_Log_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Bug Log",
        formData,
        settings: activeSettings || {},
        templateType: "Bug Log",
        templateId: "bug-log",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addBug = () => {
    const newId = `BUG-${String(formData.bugs.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      bugs: [...formData.bugs, { bug_id: newId, title: '', description: '', steps_to_reproduce: '', expected_result: '', actual_result: '', severity: 'Medium', priority: 'Medium', status: 'Open', environment: '', browser: '', reported_by: '', reported_date: new Date().toISOString().split('T')[0], assigned_to: '', fixed_date: '', verified_date: '', notes: '' }]
    });
  };

  const updateBug = (index, field, value) => {
    const bugs = [...formData.bugs];
    bugs[index] = { ...bugs[index], [field]: value };
    setFormData({ ...formData, bugs });
  };

  const removeBug = (index) => {
    setFormData({ ...formData, bugs: formData.bugs.filter((_, i) => i !== index) });
  };

  const severities = ['Critical', 'High', 'Medium', 'Low'];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const statuses = ['Open', 'In Progress', 'Fixed', 'Verified', 'Closed', 'Reopened', 'Backlog', 'Won\'t Fix'];

  const getStatusColor = (status) => {
    const colors = { 'Open': 'bg-red-100', 'In Progress': 'bg-blue-100', 'Fixed': 'bg-yellow-100', 'Verified': 'bg-green-100', 'Closed': 'bg-gray-100', 'Reopened': 'bg-orange-100', 'Backlog': 'bg-purple-100' };
    return colors[status] || 'bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Bug Log</h1><p className="text-sm text-gray-500">Bug tracking and management</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Bug Log</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="QA Team" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Bugs ({formData.bugs.length})</Label>
              <div className="space-y-4 mt-3">
                {formData.bugs.map((bug, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${getStatusColor(bug.status)}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-white px-2 py-1 rounded">{bug.bug_id}</span>
                        <span className={`text-xs px-2 py-1 rounded ${bug.severity === 'Critical' ? 'bg-red-200 text-red-800' : bug.severity === 'High' ? 'bg-orange-200 text-orange-800' : 'bg-gray-200'}`}>{bug.severity}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeBug(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2"><Label className="text-xs">Title *</Label><Input value={bug.title} onChange={(e) => updateBug(idx, 'title', e.target.value)} placeholder="Bug title" className="text-sm" /></div>
                      <div className="md:col-span-2"><Label className="text-xs">Description</Label><Textarea value={bug.description} onChange={(e) => updateBug(idx, 'description', e.target.value)} placeholder="Bug description" rows={2} className="text-sm" /></div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                      <div><Label className="text-xs">Severity</Label>
                        <Select value={bug.severity} onValueChange={(v) => updateBug(idx, 'severity', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{severities.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Priority</Label>
                        <Select value={bug.priority} onValueChange={(v) => updateBug(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={bug.status} onValueChange={(v) => updateBug(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Reported By</Label><Input value={bug.reported_by} onChange={(e) => updateBug(idx, 'reported_by', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Assigned To</Label><Input value={bug.assigned_to} onChange={(e) => updateBug(idx, 'assigned_to', e.target.value)} placeholder="Name" className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addBug} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Bug</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BugLogForm;
